import { MapPin, Car, Users, Calendar, Clock, Luggage } from 'lucide-react';

const VoucherTransfer = ({ voucherData, t, formatDate, styles, lang }) => {
  const formatTime = (timeString) => {
    if (!timeString) return t('notAvailable');
    try {
      const [hours, minutes] = timeString.split(':');
      return `${hours}:${minutes}`;
    } catch {
      return t('notAvailable');
    }
  };

  const formatFullDateTime = (dateString, timeString) => {
    if (!dateString || !timeString) return t('notAvailable');
    try {
      const date = new Date(`${dateString}T${timeString}`);
      return date.toLocaleString(lang === 'fr' ? 'fr-FR' : lang === 'es' ? 'es-ES' : 'en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return t('notAvailable');
    }
  };

  return (
    <div className="pdf-section">
      <div style={styles.section}>
        <div style={styles.sectionTitle}>
          <Car size={20} />
          {t('transferInformation')}
        </div>

        {/* Informations générales du transfert */}
        <div style={styles.infoGrid}>
          <div style={styles.infoItem}>
            <div style={styles.infoLabel}>{t('transferType')}</div>
            <div style={styles.infoValue}>
              
              {voucherData.booking_transfer?.type === 'Oneway' ? t('oneWayTransfer') :
                voucherData.booking_transfer?.type === 'Roundtrip' ? t('roundTripTransfer') :
                  voucherData.booking_transfer?.type || t('notAvailable')}
            </div>
          </div>

          <div style={styles.infoItem}>
            <div style={styles.infoLabel}>{t('travelType')}</div>
            <div style={styles.infoValue}>
              {voucherData.booking_transfer?.itineraries?.[0]?.travelType === 'flight' ? t('flightTransfer') :
                voucherData.booking_transfer?.itineraries?.[0]?.travelType === 'train' ? t('trainTransfer') :
                  voucherData.booking_transfer?.itineraries?.[0]?.travelType || t('notAvailable')}
            </div>
          </div>

          {voucherData.booking_transfer?.itineraries?.[0]?.travelNumber && (
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>{t('travelNumber')}</div>
              <div style={styles.infoValue}>
                {voucherData.booking_transfer.itineraries[0].travelNumber}
              </div>
            </div>
          )}
        </div>
        <div style={{ ...styles.infoItem, marginTop: '16px' }}>
          <div style={styles.infoLabel}> {t('passengers')}</div>
          <div style={styles.infoValue}>
            
            {voucherData.booking_transfer?.adult > 0 && (
              <><span>{t('adults')}: {voucherData.booking_transfer?.adult}</span></>
            )}
            {voucherData.booking_transfer?.child > 0 && (
              <><span style={{ margin: '0 12px' }}>•</span><span>{t('children')}: {voucherData.booking_transfer?.child}</span></>
            )}
            {voucherData.booking_transfer?.infant > 0 && (
              <><span style={{ margin: '0 12px' }}>•</span><span>{t('infants')}: {voucherData.booking_transfer?.infant}</span></>
            )}
            {voucherData.booking_transfer?.senior > 0 && (
              <><span style={{ margin: '0 12px' }}>•</span><span>{t('seniors')}: {voucherData.booking_transfer?.senior}</span></>
            )}
            {voucherData.booking_transfer?.baby > 0 && (
              <><span style={{ margin: '0 12px' }}>•</span><span>{t('baby')}: {voucherData.booking_transfer?.baby}</span></>
            )}
          </div>
        </div>

        {/* Informations des itinéraires */}
        {voucherData.booking_transfer?.itineraries && voucherData.booking_transfer.itineraries.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            {voucherData.booking_transfer.itineraries.map((itinerary, index) => (
              <div key={index} style={{
                marginBottom: index < voucherData.booking_transfer.itineraries.length - 1 ? '32px' : '0',
                padding: '20px',
                background: '#f8fafc',
                borderRadius: '10px',
                border: '1px solid #e5e7eb'
              }}>
                {/* Header de l'itinéraire */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '10px',
                  borderBottom: '2px solid #7c3aed'
                }}>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                    {t('itinerary')} {index + 1}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>
                    {formatFullDateTime(itinerary.transferDate, itinerary.transferTime)}
                  </div>
                </div>

                {/* Informations détaillées */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', alignItems: 'center' }}>
                  {/* Origine */}
                  <div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#6b7280',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <MapPin size={14} />
                      {t('origin')}
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                      {itinerary.origin?.name || t('notAvailable')}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      {t(itinerary.origin?.type?.toLowerCase() || 'location')}
                    </div>
                  </div>


                  {/* Destination(s) */}
                  <div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#6b7280',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <MapPin size={14} />
                      {t('destination')}
                    </div>
                    {itinerary.destination && itinerary.destination.length > 0 ? (
                      itinerary.destination.map((dest, destIndex) => (
                        <div key={destIndex} style={{ marginBottom: destIndex < itinerary.destination.length - 1 ? '12px' : '0' }}>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                            {dest.name}
                          </div>
                          <div style={{ fontSize: '14px', color: '#6b7280' }}>
                            {t(dest.type?.toLowerCase() || 'location')}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                        {t('notAvailable')}
                      </div>
                    )}
                  </div>
                </div>

                {/* Véhicule */}
                {itinerary.vehicle && (
                  <div style={{
                    marginTop: '24px',
                    padding: '16px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db'
                  }}>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1f2937',
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Car size={18} />
                      {t('vehicleInformation')}
                    </div>
                    <div style={styles.infoGrid}>
                      <div style={styles.infoItem}>
                        <div style={styles.infoLabel}>{t('vehicleType')}</div>
                        <div style={styles.infoValue}>
                          {itinerary.vehicle.type}
                        </div>
                      </div>
                      <div style={styles.infoItem}>
                        <div style={styles.infoLabel}>{t('brand')}</div>
                        <div style={styles.infoValue}>
                          {itinerary.vehicle.brand}
                        </div>
                      </div>
                      <div style={styles.infoItem}>
                        <div style={styles.infoLabel}>{t('model')}</div>
                        <div style={styles.infoValue}>
                          {itinerary.vehicle.model}
                        </div>
                      </div>
                      <div style={styles.infoItem}>
                        <div style={styles.infoLabel}>
                          <Luggage size={14} style={{ marginRight: '4px' }} />
                          {t('luggageCapacity')}
                        </div>
                        <div style={styles.infoValue}>
                          {itinerary.vehicle.luggageCapacity} {t('bags')}
                        </div>
                      </div>
                      <div style={styles.infoItem}>
                        <div style={styles.infoLabel}>
                          <Users size={14} style={{ marginRight: '4px' }} />
                          {t('passengerCapacity')}
                        </div>
                        <div style={styles.infoValue}>
                          {itinerary.vehicle.capacity} {t('persons')}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Date et heure */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  alignItems: 'center',
                  marginTop: '20px',
                  paddingTop: '16px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Calendar size={16} color="#6b7280" />
                    <div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>{t('date')}</div>
                      <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
                        {itinerary.transferDate ? formatDate(itinerary.transferDate) : t('notAvailable')}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Clock size={16} color="#6b7280" />
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>{t('time')}</div>
                      <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
                        {itinerary.transferTime ? formatTime(itinerary.transferTime) : t('notAvailable')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VoucherTransfer;